import { WebSocketMessage } from '../../../types';
import { triggerGlobalLogout, getGlobalViewAsRole } from '../../../shared/api/apiClient';

// WebSocket close codes for auth errors (standard + custom)
const AUTH_ERROR_CODES = [
  4001, // Custom: Unauthorized
  4003, // Custom: Forbidden
  4401, // Custom: Token expired
];

export class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private messageHandlers: Map<string, Set<(data: WebSocketMessage) => void>> = new Map();
  private isConnecting = false;
  private connectPromise: Promise<void> | null = null;
  private pendingReject: ((reason: Error) => void) | null = null;
  // Bumped on every new/superseding connect and on disconnect. Socket event
  // handlers and scheduled reconnects capture their generation and no-op once
  // superseded, so a socket can never stay bound to the wrong investigation.
  private connectionGeneration = 0;
  private sessionId: string = '';
  private customerId: string = '';
  private userId: string = '';
  private token: string | undefined = undefined;
  private shouldReconnect = true;
  private messageQueue: Array<{ action: string; payload: Record<string, unknown> }> = [];
  private connectionStateHandler: ((connected: boolean, isReconnecting: boolean) => void) | null = null;
  private tokenRefreshHandler: (() => Promise<string | null>) | null = null;

  constructor(private wsUrl: string) {
    // WebSocket service initialized
  }

  /**
   * Set handler for token refresh - called before reconnect attempts
   * Returns new token or null if refresh failed
   */
  onTokenRefresh(handler: () => Promise<string | null>) {
    this.tokenRefreshHandler = handler;
  }

  /**
   * Update the token (called after auth refresh)
   */
  updateToken(newToken: string) {
    this.token = newToken;
  }

  onConnectionStateChange(handler: (connected: boolean, isReconnecting: boolean) => void) {
    this.connectionStateHandler = handler;
  }

  // A superseded/torn-down connect must reject its promise, or `await connect()` hangs forever.
  private abortPendingConnect(reason: string): void {
    this.pendingReject?.(new Error(reason));
    this.pendingReject = null;
    this.connectPromise = null;
    this.isConnecting = false;
  }

  connect(sessionId: string, actorId: string, customerId: string, token?: string): Promise<void> {
    // Already connected to the requested investigation — nothing to do.
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.sessionId === sessionId) {
      return Promise.resolve();
    }
    // A connect for the SAME investigation is already in flight — hand back that
    // same promise so callers await the real socket open. (Returning
    // Promise.resolve() here let a seeded message send mid-handshake and fail
    // with "WebSocket not connected".) A connect for a DIFFERENT investigation
    // must NOT reuse it, or the socket stays bound to the wrong session.
    if (this.isConnecting && this.connectPromise && this.sessionId === sessionId) {
      return this.connectPromise;
    }

    // Supersede any socket open or connecting for a different investigation.
    const generation = ++this.connectionGeneration;
    this.abortPendingConnect('WebSocket connect superseded');
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.sessionId = sessionId;
    this.customerId = customerId;
    this.userId = actorId;
    this.token = token;
    this.shouldReconnect = true;

    this.isConnecting = true;

    this.connectPromise = new Promise((resolve, reject) => {
      this.pendingReject = reject;
      // The tenant is stated by the client; the server authorizes against it
      // rather than inferring it from the investigation.
      let url = `${this.wsUrl}?session_id=${sessionId}&actor_id=${actorId}&customer_id=${encodeURIComponent(customerId)}`;
      if (token) {
        url += `&token=${encodeURIComponent(token)}`;
      }
      const viewAsRole = getGlobalViewAsRole();
      if (viewAsRole) {
        url += `&view_as=${encodeURIComponent(viewAsRole)}`;
      }
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        if (generation !== this.connectionGeneration) return;
        this.reconnectAttempts = 0;
        this.isConnecting = false;
        this.connectPromise = null;
        this.pendingReject = null;
        this.connectionStateHandler?.(true, false);

        // Connection established via $connect WebSocket route - no message needed
        // The backend validates JWT and creates connection record via query parameters
        this.flushMessageQueue();
        resolve();
      };

      this.ws.onmessage = (event) => {
        if (generation !== this.connectionGeneration) return;
        try {
          const data = JSON.parse(event.data) as WebSocketMessage;

          if (!data.type) {
            return;
          }

          const handlers = this.messageHandlers.get(data.type);
          if (handlers) {
            handlers.forEach(handler => handler(data));
          }
        } catch (error) {
          console.error('[WebSocketService] Error parsing WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        if (generation !== this.connectionGeneration) return;
        this.isConnecting = false;
        if (this.reconnectAttempts === 0) {
          this.connectPromise = null;
          this.pendingReject = null;
          reject(error);
        } else {
          // Notify user of connection errors during reconnection attempts
          this.connectionStateHandler?.(false, true);
        }
      };

      this.ws.onclose = (event) => {
        // A superseded socket (investigation switched) must not drive reconnects.
        if (generation !== this.connectionGeneration) return;
        this.isConnecting = false;

        // Check if closed due to auth error
        if (AUTH_ERROR_CODES.includes(event.code)) {
          console.log('[WebSocketService] Connection closed due to auth error, code:', event.code);
          this.handleAuthError(generation);
          return;
        }

        if (this.shouldReconnect) {
          this.handleReconnect(sessionId, actorId, customerId, generation);
        } else {
          this.connectionStateHandler?.(false, false);
        }
      };
    });
    return this.connectPromise;
  }

  sendMessage(action: string, payload: Record<string, unknown>): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ action, ...payload }));

        if (action === 'truncateHistory') {
          const handler = (_data: WebSocketMessage) => {
            this.off('truncateAck');
            resolve();
          };
          this.on('truncateAck', handler);

          setTimeout(() => {
            this.off('truncateAck');
            resolve();
          }, 1000);
        } else {
          resolve();
        }
      } else if (this.reconnectAttempts > 0 && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.messageQueue.push({ action, payload });
        resolve();
      } else {
        // Notify user about lost messages when max reconnects exceeded
        const errorHandlers = this.messageHandlers.get('error');
        if (errorHandlers && this.messageQueue.length > 0) {
          const messageCount = this.messageQueue.length;
          errorHandlers.forEach(handler => handler({
            type: 'error',
            code: 'MESSAGE_QUEUE_LOST',
            message: `Connection lost. ${messageCount} queued message${messageCount > 1 ? 's' : ''} could not be sent.`,
            timestamp: new Date().toISOString(),
            investigation_id: this.sessionId || ''
          }));
        }
        reject(new Error('WebSocket not connected'));
      }
    });
  }

  private flushMessageQueue(): void {
    if (this.messageQueue.length > 0) {
      const queue = [...this.messageQueue];
      this.messageQueue = [];

      queue.forEach(({ action, payload }) => {
        this.sendMessage(action, payload).catch(() => {});
      });
    }
  }

  on(eventType: string, handler: (data: WebSocketMessage) => void): void {
    let handlers = this.messageHandlers.get(eventType);
    if (!handlers) {
      handlers = new Set();
      this.messageHandlers.set(eventType, handlers);
    }
    handlers.add(handler);
  }

  off(eventType: string, handler?: (data: WebSocketMessage) => void): void {
    if (handler) {
      const handlers = this.messageHandlers.get(eventType);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.messageHandlers.delete(eventType);
        }
      }
    } else {
      this.messageHandlers.delete(eventType);
    }
  }

  /**
   * Handle auth errors - try to refresh token, if that fails trigger logout
   */
  private async handleAuthError(generation: number): Promise<void> {
    this.connectionStateHandler?.(false, true);

    // Try to refresh the token
    if (this.tokenRefreshHandler) {
      try {
        const newToken = await this.tokenRefreshHandler();
        if (newToken) {
          this.token = newToken;
          // Retry connection with new token
          this.reconnectAttempts = 0;
          this.connect(this.sessionId, this.userId, this.customerId, newToken).catch(() => {
            // If reconnect fails after refresh, trigger logout
            triggerGlobalLogout();
          });
          return;
        }
      } catch (error) {
        console.error('[WebSocketService] Token refresh failed:', error);
      }
    }

    // Settle the in-flight connect before logout (unless a switch during the refresh
    // await already superseded it), or its awaiter hangs forever.
    if (generation === this.connectionGeneration) {
      this.abortPendingConnect('WebSocket auth error');
    }
    // Token refresh failed or not available - trigger centralized logout
    triggerGlobalLogout();
  }

  private handleReconnect(sessionId: string, actorId: string, customerId: string, generation: number): void {
    // Superseded by a newer connection (investigation switched) — stop reconnecting.
    if (generation !== this.connectionGeneration) {
      return;
    }
    if (!this.shouldReconnect) {
      this.connectionStateHandler?.(false, false);
      return;
    }

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;

      const baseDelay = 1000;
      const maxDelay = 30000;
      const delay = Math.min(baseDelay * Math.pow(2, this.reconnectAttempts - 1), maxDelay);

      this.connectionStateHandler?.(false, true);

      setTimeout(() => {
        if (generation !== this.connectionGeneration) return;
        this.connect(sessionId, actorId, customerId, this.token).catch(() => {});
      }, delay);
    } else {
      this.connectionStateHandler?.(false, false);

      const errorHandlers = this.messageHandlers.get('error');
      if (errorHandlers) {
        errorHandlers.forEach(handler => handler({
          type: 'error',
          code: 'CONNECTION_NOT_FOUND',
          message: 'Unable to maintain connection. Please refresh the page.',
          timestamp: new Date().toISOString(),
          investigation_id: this.sessionId || ''
        }));
      }
    }
  }

  disconnect(preventReconnect: boolean = true): void {
    this.shouldReconnect = !preventReconnect;
    // Invalidate the socket being torn down so its pending onclose/onmessage
    // handlers no-op instead of driving a reconnect for the old investigation.
    this.connectionGeneration++;
    this.abortPendingConnect('WebSocket disconnected');

    if (preventReconnect) {
      this.reconnectAttempts = this.maxReconnectAttempts;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    if (preventReconnect) {
      this.messageHandlers.clear();
      this.messageQueue = [];
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /** Whether the socket is open, or opening, for this investigation. */
  isBoundTo(sessionId: string): boolean {
    return this.sessionId === sessionId && (this.isConnected() || this.isConnecting);
  }
}

export default WebSocketService;